import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import posthog from "posthog-js";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Modal, Button, Form, Icon } from "semantic-ui-react";
import { axiosBasic } from "../../../api/axios";
import { config } from "../../../api/endPoints";
import { fetchSubscriptionData } from "../../../utils/subscriptionUtils";
import { WebStorage, WebStorageNames } from "../../../utils";
import { Common } from "../../../utils";
import "./TrialPeriodMessage.css";

const TrialPeriodMessage = ({ contentAreaLeft = 0 }) => {
    const { t } = useTranslation();
    const [subscriptionData, setSubscriptionData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showDemoModal, setShowDemoModal] = useState(false);
    const [demoDate, setDemoDate] = useState(null);
    const [demoTime, setDemoTime] = useState("09:00");
    const [demoSubmitting, setDemoSubmitting] = useState(false);
    const [showTimeDropdown, setShowTimeDropdown] = useState(false);
    const timeDropdownRef = useRef(null);

    // Time options every 30 min: 12:00 AM through 11:30 PM (value in 24h "HH:mm")
    const timeOptions = React.useMemo(() => {
        const opts = [];
        for (let h = 0; h < 24; h++) {
            for (let m = 0; m < 60; m += 30) {
                const value = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
                const hour12 = h % 12 || 12;
                const period = h < 12 ? 'AM' : 'PM';
                const label = `${hour12}:${m.toString().padStart(2, '0')} ${period}`;
                opts.push({ value, label });
            }
        }
        return opts;
    }, []);

    const formatTimeDisplay = (value24) => {
        if (!value24) return t("app.trialBar.selectTime");
        const [h, m] = value24.split(':').map(Number);
        const hour12 = h % 12 || 12;
        const period = h < 12 ? 'AM' : 'PM';
        return `${hour12}:${m.toString().padStart(2, '0')} ${period}`;
    };

    const fetchSubscription = useCallback(async () => {
        try {
            setLoading(true);
            const data = await fetchSubscriptionData();
            if (data) {
                setSubscriptionData(data);
            }
        } catch (err) {
            if (typeof posthog !== "undefined" && posthog.capture) {
                posthog.capture("trialPeriodMessageSubscription", {
                    type: "exception",
                    success: false,
                    reason: err?.response?.data?.message ?? err?.message ?? "Request failed",
                    status: err?.response?.status,
                });
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchSubscription();
    }, [fetchSubscription]);

    useEffect(() => {
        if (!showTimeDropdown) return;
        const handleClickOutside = (e) => {
            if (timeDropdownRef.current && !timeDropdownRef.current.contains(e.target)) {
                setShowTimeDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showTimeDropdown]);

    // Get roleId to check if user is admin (Role 1 or 2)
    let roleId = null;
    try {
        roleId = WebStorage.getLocalStore(WebStorageNames.RoleId);
    } catch (e) {
        roleId = localStorage.getItem("roleId");
    }

    const isAdmin = roleId && (parseInt(roleId) === 1 || parseInt(roleId) === 2);

    // If not an admin, don't show any trial/subscription message here
    // Non-admin subscription messages are handled by SubscriptionRestriction or during login
    if (!isAdmin) return null;

    const formatTrialEndDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const getUserTimezone = () => {
        try {
            return Intl.DateTimeFormat().resolvedOptions().timeZone;
        } catch (e) {
            return '';
        }
    };

    const getTimezoneShortLabel = () => {
        try {
            const parts = new Date().toLocaleTimeString('en-US', { timeZoneName: 'short' }).split(' ');
            return parts.length >= 2 ? parts.slice(-1)[0] : getUserTimezone();
        } catch (e) {
            return getUserTimezone();
        }
    };

    /** Full timezone name for email/API (e.g. "Eastern Standard Time") */
    const getTimezoneLongLabel = () => {
        try {
            const tz = getUserTimezone();
            if (!tz) return '';
            const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'long' }).formatToParts(new Date());
            const tzPart = parts.find((p) => p.type === 'timeZoneName');
            return tzPart ? tzPart.value : getTimezoneShortLabel();
        } catch (e) {
            return getTimezoneShortLabel();
        }
    };

    const getCombinedDemoDateTime = () => {
        if (!demoDate || !demoTime) return null;
        const [hours, minutes] = demoTime.split(':').map(Number);
        return new Date(
            demoDate.getFullYear(),
            demoDate.getMonth(),
            demoDate.getDate(),
            hours,
            minutes
        );
    };

    const formatDemoDateTimeForDescription = (date) => {
        if (!date) return '';
        const d = new Date(date);
        const dateStr = d.toLocaleDateString('en-US', {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
            year: 'numeric'
        });
        const timeStr = d.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
        const tzShort = getTimezoneShortLabel();
        const tzLong = getTimezoneLongLabel();
        const tzPart = tzShort && tzLong
            ? ` (${tzShort} - ${tzLong})`
            : tzShort
                ? ` (${tzShort})`
                : tzLong
                    ? ` (${tzLong})`
                    : '';
        return `Requested demo slot: ${dateStr} at ${timeStr}${tzPart}`;
    };

    const handleRequestDemoSubmit = async () => {
        const combined = getCombinedDemoDateTime();
        if (!combined) {
            Common.showErrorMessage(t("app.trialBar.pleaseSelectDateAndTime"));
            return;
        }
        setDemoSubmitting(true);
        try {
            const description = formatDemoDateTimeForDescription(combined);
            const payload = {
                title: t("app.trialBar.requestADemo"),
                description,
                problemType: "Other",
                attachments: [],
            };
            const response = await axiosBasic.post(
                config.REST_API.Account.ContactUs,
                payload
            );
            if (response.data && response.data.success !== false) {
                Common.showSuccessMessage(
                    t("app.trialBar.demoRequestSubmitted")
                );
                setShowDemoModal(false);
                setDemoDate(null);
                setDemoTime("09:00");
            } else {
                Common.showErrorMessage(
                    response.data?.message || "Failed to submit your request. Please try again."
                );
            }
        } catch (error) {
            if (typeof posthog !== "undefined" && posthog.capture) {
                posthog.capture("trialRequestDemoContactUs", {
                    type: "exception",
                    success: false,
                    reason: error?.response?.data?.message ?? error?.message ?? "Request failed",
                    status: error?.response?.status,
                });
            }
            Common.showErrorMessage(
                error?.response?.data?.message || "An error occurred. Please try again."
            );
        } finally {
            setDemoSubmitting(false);
        }
    };

    const handleCloseDemoModal = () => {
        if (!demoSubmitting) {
            setShowDemoModal(false);
            setDemoDate(null);
            setDemoTime("09:00");
        }
    };

    if (loading) return null;

    const subscription = subscriptionData?.subscription;
    const isInTrial = subscriptionData?.isInTrial || false;
    const trialDaysRemaining = subscriptionData?.trialDaysRemaining ?? 0;
    const subscriptionStatus = subscription?.status ?? -1;
    const isActive = subscriptionData?.isActive ?? false;
    const isCancelledButBillingActive = subscriptionData?.isCancelledButBillingActive ?? false;

    // Check if subscription is restricted (Suspended, Cancelled, or Expired)
    // But don't restrict if subscription is active or cancelled-but-billing-active
    const isSuspended = (isActive || isCancelledButBillingActive) ? false : (
        (subscriptionStatus === 2 && trialDaysRemaining === 0) ||
        (subscriptionStatus === 3 && !isCancelledButBillingActive) ||
        subscriptionStatus === 4
    );

    // Get status-specific message
    const getStatusMessage = () => {
        // Handle cancelled-but-active scenario
        if (isCancelledButBillingActive) {
            const daysRemaining = subscriptionData?.daysRemainingInBillingPeriod ?? 0;
            const billingPeriodEnd = subscriptionData?.billingPeriodEnd;
            const endDate = billingPeriodEnd
                ? new Date(billingPeriodEnd).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                })
                : 'the end of your billing period';

            if (daysRemaining > 0) {
                return `Your subscription has been cancelled, but you'll continue to have access until ${endDate} (${daysRemaining} ${daysRemaining === 1 ? 'day' : 'days'} remaining).`;
            } else {
                return `Your subscription has been cancelled. Access will end on ${endDate}.`;
            }
        }

        if (subscriptionStatus === 2 && trialDaysRemaining === 0) {
            return "Your subscription has been suspended. Please update your payment information to continue using the service.";
        }
        if (subscriptionStatus === 3 && !isCancelledButBillingActive) {
            return "Your subscription has been cancelled. Please update your payment information to reactivate your subscription.";
        }
        if (subscriptionStatus === 4) {
            return "Your subscription has expired. Please update your payment information to renew your subscription.";
        }
        return "Your subscription is not active. Please update your payment information to continue using the service.";
    };

    // Show restricted message if subscription is suspended, cancelled, or expired
    if (isSuspended) {
        return (
            <div style={{
                position: 'fixed',
                bottom: 0,
                left: contentAreaLeft,
                right: 0,
                color: '#333',
                padding: '14px 24px',
                textAlign: 'center',
                boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.08)',
                zIndex: 9999,
                backgroundColor: '#fef2f2',
                borderTop: '2px solid #dc2626'
            }}>
                <p style={{ margin: 0, fontSize: '0.95rem', fontWeight: 600, color: '#b91c1c' }}>
                    {getStatusMessage()}{' '}
                    <Link
                        to="/settings/company?tab=billing"
                        style={{
                            color: '#0981c4',
                            textDecoration: 'underline',
                            fontWeight: 600
                        }}
                    >
                        {t("app.trialBar.updatePayment")}
                    </Link>
                </p>
            </div>
        );
    }

    // Show trial message if still in trial
    if (isInTrial && subscription?.trialEndDate) {
        return (
            <>
                <div
                    className="trial-period-message-bar"
                    style={{
                        position: 'fixed',
                        bottom: 0,
                        left: contentAreaLeft,
                        right: 0,
                        color: '#333',
                        padding: '14px 24px',
                        boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.08)',
                        zIndex: 9999,
                        backgroundColor: '#f8fafc',
                        borderTop: '2px solid #e2e8f0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: '12px',
                        minHeight: '52px',
                        boxSizing: 'border-box'
                    }}
                >
                    <p style={{ margin: 0, fontSize: '0.95rem', fontWeight: 500, color: '#334155', flex: '1 1 auto', minWidth: 0, textAlign: 'center' }}>
                        <span style={{ color: '#334155' }}>{t("app.trialBar.yourTrialWillEndOn")}</span>
                        <strong style={{ color: '#0f172a' }}>{formatTrialEndDate(subscription.trialEndDate)}</strong>
                        <span style={{ color: '#334155' }}>. </span>
                        <Link
                            to="/settings/company?tab=billing"
                            style={{
                                color: '#0981c4',
                                textDecoration: 'underline',
                                textUnderlineOffset: '2px',
                                fontWeight: 600
                            }}
                        >
                            {t("app.trialBar.manageBilling")}
                        </Link>
                    </p>
                    <button
                        type="button"
                        onClick={() => setShowDemoModal(true)}
                        style={{
                            background: '#0981c4',
                            border: 'none',
                            color: '#fff',
                            padding: '8px 18px',
                            borderRadius: '8px',
                            fontSize: '0.9rem',
                            fontWeight: 600,
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                            flexShrink: 0
                        }}
                    >
                        {t("app.trialBar.requestADemo")}
                    </button>
                </div>

                <Modal
                    open={showDemoModal}
                    onClose={handleCloseDemoModal}
                    size="small"
                    closeOnDimmerClick={!demoSubmitting}
                    closeOnEscape={!demoSubmitting}
                    className="request-demo-modal"
                >
                    <div
                        className="modal-header-brand request-demo-modal-header"
                        style={{
                            padding: '20px 24px',
                            paddingRight: 52,
                            position: 'relative',
                            borderRadius: '12px 12px 0 0'
                        }}
                    >
                        <button
                            type="button"
                            aria-label="Close"
                            onClick={handleCloseDemoModal}
                            disabled={demoSubmitting}
                            className="request-demo-close-btn"
                            style={{
                                position: 'absolute',
                                top: 20,
                                right: 16,
                                width: 32,
                                height: 32,
                                borderRadius: '50%',
                                border: 'none',
                                background: 'rgba(255,255,255,0.2)',
                                color: 'white',
                                cursor: demoSubmitting ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: 0,
                                margin: 0
                            }}
                        >
                            <Icon name="close" style={{ margin: 0 }} />
                        </button>
                        <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600, color: 'white' }}>
                            {t("app.trialBar.requestADemo")}
                        </h3>
                        <p style={{ margin: '4px 0 0 0', fontSize: '0.9rem', opacity: 0.9, color: 'white' }}>
                            {t("app.trialBar.chooseDateAndTime")}
                        </p>
                    </div>
                    <Modal.Content style={{ padding: '24px' }}>
                        <Form>
                            <Form.Field>
                                <label style={{ fontWeight: 600, color: '#334155', marginBottom: 8 }}>{t("app.trialBar.date")}</label>
                                <div className="request-demo-datepicker-wrapper">
                                    <DatePicker
                                        selected={demoDate}
                                        onChange={setDemoDate}
                                        dateFormat="MMMM d, yyyy"
                                        placeholderText={t("app.trialBar.selectDate")}
                                        minDate={new Date()}
                                        popperPlacement="bottom-start"
                                        popperClassName="request-demo-popper"
                                        calendarClassName="request-demo-calendar"
                                        wrapperClassName="request-demo-datepicker"
                                    />
                                </div>
                            </Form.Field>
                            <Form.Field>
                                <label style={{ fontWeight: 600, color: '#334155', marginBottom: 8 }}>{t("app.trialBar.time")}</label>
                                <div ref={timeDropdownRef} className="request-demo-time-dropdown" style={{ position: 'relative' }}>
                                    <button
                                        type="button"
                                        onClick={() => setShowTimeDropdown((v) => !v)}
                                        className="request-demo-time-trigger"
                                        style={{
                                            width: '100%',
                                            padding: '10px 14px',
                                            border: '1px solid #e2e8f0',
                                            borderRadius: 8,
                                            fontSize: '0.95rem',
                                            textAlign: 'left',
                                            background: '#fff',
                                            color: '#334155',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            boxSizing: 'border-box'
                                        }}
                                    >
                                        <span>{formatTimeDisplay(demoTime)}</span>
                                        <Icon name={showTimeDropdown ? 'chevron up' : 'chevron down'} style={{ marginLeft: 8, flexShrink: 0 }} />
                                    </button>
                                    {showTimeDropdown && (
                                        <div
                                            className="request-demo-time-options"
                                            style={{
                                                position: 'absolute',
                                                top: '100%',
                                                left: 0,
                                                right: 0,
                                                marginTop: 4,
                                                maxHeight: 220,
                                                overflowY: 'auto',
                                                background: '#fff',
                                                border: '1px solid #e2e8f0',
                                                borderRadius: 8,
                                                boxShadow: '0 10px 40px rgba(0,0,0,0.12)',
                                                zIndex: 10002
                                            }}
                                        >
                                            {timeOptions.map((opt) => (
                                                <button
                                                    key={opt.value}
                                                    type="button"
                                                    onClick={() => {
                                                        setDemoTime(opt.value);
                                                        setShowTimeDropdown(false);
                                                    }}
                                                    style={{
                                                        display: 'block',
                                                        width: '100%',
                                                        padding: '10px 14px',
                                                        border: 'none',
                                                        background: demoTime === opt.value ? '#e0f2fe' : 'transparent',
                                                        color: demoTime === opt.value ? '#0369a1' : '#334155',
                                                        fontSize: '0.95rem',
                                                        textAlign: 'left',
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    {opt.label}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </Form.Field>
                            <p style={{ margin: '8px 0 0 0', fontSize: '0.85rem', color: '#64748b' }}>
                                {t("app.trialBar.yourTimezone")}<strong>{getTimezoneShortLabel()}</strong>
                                {getUserTimezone() && getUserTimezone() !== getTimezoneShortLabel() && (
                                    <span> ({getUserTimezone()})</span>
                                )}
                            </p>
                            <p style={{ margin: '16px 0 0 0', paddingTop: 16, borderTop: '1px solid #e2e8f0', fontSize: '0.9rem', color: '#334155' }}>
                                {t("app.trialBar.learnHowToUseVideo")}{" "}
                                <a
                                    href="https://www.youtube.com/watch?v=rRYWE6q73N4"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ color: '#006AC5', fontWeight: 600, textDecoration: 'none' }}
                                >
                                    {t("app.trialBar.watchThisVideo")}
                                </a>
                            </p>
                        </Form>
                    </Modal.Content>
                    <Modal.Actions style={{ padding: '16px 24px', borderTop: '1px solid #e2e8f0', backgroundColor: '#f8fafc', borderRadius: '0 0 12px 12px' }}>
                        <Button
                            onClick={handleCloseDemoModal}
                            disabled={demoSubmitting}
                            style={{
                                background: 'white',
                                color: '#64748b',
                                border: '1px solid #cbd5e1'
                            }}
                        >
                            {t("app.trialBar.cancel")}
                        </Button>
                        <Button
                            className="btn-brand"
                            loading={demoSubmitting}
                            disabled={!getCombinedDemoDateTime() || demoSubmitting}
                            onClick={handleRequestDemoSubmit}
                            style={{
                                background: '#006AC5',
                                color: 'white',
                                border: 'none'
                            }}
                        >
                            {t("app.trialBar.submitRequest")}
                        </Button>
                    </Modal.Actions>
                </Modal>
            </>
        );
    }

    return null;
};

export default TrialPeriodMessage;

